/**
 * Public + admin config endpoints.
 *
 * Extracted from `src/services/api.ts` (Fase 10 Slice 10.3).
 *   - GET /health                — backend liveness
 *   - GET /app-config            — public funnel runtime config
 *   - POST /admin/config/bootstrap     — seed default config
 *   - GET  /admin/config/{key}         — read a config section
 *   - PUT  /admin/config/{key}         — upsert a config section
 *
 * Re-exported from services/api.ts so existing call-sites keep working.
 */

import { apiClient } from './client';
import type { AppConfigResponse, BreedDatabaseConfig } from '../types/config';

// ─── Shared admin payload shapes ────────────────────────────────────────────

export interface AdminConfigItemResponse {
  key: string;
  value: unknown;
  description?: string | null;
}

export interface AdminConfigUpsertPayload {
  value: unknown;
  description?: string;
  /**
   * Optional human-readable rationale stored in system_config_history.
   * Surfaces in the "Historial de configuración" tab (Fase 3).
   */
  reason?: string;
}

// ─── Public endpoints ───────────────────────────────────────────────────────

/**
 * Backend health probe. Throws when the backend is unreachable.
 */
export const healthCheck = async (): Promise<{ status: string }> => {
  try {
    const response = await apiClient.get('/health');
    return response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Backend health check failed:', error);
    throw new Error('El backend no está disponible.');
  }
};

/**
 * Public runtime config consumed by the funnel — pricing rules,
 * supplements, recipes, shipping, runtime feature switches, etc.
 */
export const getAppConfig = async (platform?: string | null): Promise<AppConfigResponse> => {
  // Forward the detected origin platform so the backend serves the per-origin
  // catalog/stock and echoes back the resolved `effective_platform` (honouring
  // force modes). The cache-buster is intentional: clinical exclusions, stock
  // and kill switches are runtime state and must update immediately after a
  // backoffice save, even for browsers that cached an older response before
  // the API started returning Cache-Control: no-store.
  const params: Record<string, string | number> = { _ts: Date.now() };
  if (platform) params.platform = platform;
  const response = await apiClient.get<AppConfigResponse>('/app-config', { params });
  return response.data;
};

/**
 * Breed database, served by its own endpoint (split out of /app-config, which no
 * longer includes it — it is ~90 KB and near-static). Fetched once per session and
 * cached in-module; concurrent callers share the in-flight promise. The backend
 * serves it with Cache-Control: no-store (server-controlled freshness via its
 * internal config TTL), so no _ts cache-buster is needed here. Returns null on
 * failure so callers fall back to the built-in defaults.
 */
let breedDatabaseCache: BreedDatabaseConfig | null = null;
let breedDatabasePromise: Promise<BreedDatabaseConfig | null> | null = null;

export const getBreedDatabase = async (): Promise<BreedDatabaseConfig | null> => {
  if (breedDatabaseCache) return breedDatabaseCache;
  if (!breedDatabasePromise) {
    breedDatabasePromise = apiClient
      .get<{ breed_database?: BreedDatabaseConfig }>('/app-config/breeds')
      .then((response) => {
        breedDatabaseCache = response.data?.breed_database ?? null;
        return breedDatabaseCache;
      })
      .catch(() => {
        breedDatabasePromise = null; // allow a retry on the next call
        return null;
      });
  }
  return breedDatabasePromise;
};

// ─── Admin endpoints ────────────────────────────────────────────────────────

/**
 * Seed the dynamic config from in-code defaults. Idempotent — only
 * inserts keys missing from system_configs.
 */
export const adminBootstrapConfig = async (): Promise<Record<string, unknown>> => {
  const response = await apiClient.post<Record<string, unknown>>('/admin/config/bootstrap', {}, {});
  return response.data;
};

export const adminGetConfigKey = async (key: string): Promise<AdminConfigItemResponse> => {
  const response = await apiClient.get<AdminConfigItemResponse>(`/admin/config/${key}`, {});
  return response.data;
};

export const adminUpsertConfigKey = async (
  key: string,
  payload: AdminConfigUpsertPayload,
): Promise<AdminConfigItemResponse> => {
  const response = await apiClient.put<AdminConfigItemResponse>(`/admin/config/${key}`, payload, {});
  return response.data;
};
